import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

export class FormControlValidators {

    static passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
        const password = control.get('password');
        const confirmPassword = control.get('passwordConfirm');
        return (password?.value !== confirmPassword?.value) ? { 'noPasswordMatch': true } : null;
    }

    static emailMatchValidator(control: AbstractControl): ValidationErrors | null {
        const email = control.get('email');
        const confirmEmail = control.get('emailConfirm');
        return (email?.value !== confirmEmail?.value) ? { 'noEmailMatch': true } : null;
    }

    static patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            const isValid = regex.test(value);
            return isValid ? null : error;
        };
    }

    static dateRangeValidator(min: Date, max: Date, error: ValidationErrors): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return error;
            const value: Date | null = new Date(control.value);
            return ( !value || value < min || value > max) ? error : null;
        };
    }

    static italianFiscalCodeValidator(error: ValidationErrors): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = (control.value || '').toUpperCase().trim();

            if (value === '') return null; // Let validator handle empty case

            if (FormControlValidators.isValidStandardFiscalCode(value) || FormControlValidators.isValidTemporaryFiscalCode(value)) {
                return null;
            }
            return error;
        }
    }

    static italianVatNumberValidator(error: ValidationErrors): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = (control.value || '').trim();

            if (value === '') return null; // Non valida se vuota, lascia required al form

            if (FormControlValidators.isValidItalianVat(value) || FormControlValidators.isValidStandardFiscalCode(value) || FormControlValidators.isValidTemporaryFiscalCode(value)) {
                return null;
            }
            return error;
        };
    }

    private static isValidItalianVat(piva: string): boolean {
        if (!/^[0-9]{11}$/.test(piva)) {
            return false; // Deve essere 11 cifre
        }
        if (parseInt(piva, 10) === 0) {
            return false; // Non può essere tutto zero
        }
        let sum = 0;
        for (let i = 0; i <= 9; i++) {
            let n = parseInt(piva.charAt(i), 10);
            if (i % 2 === 0) {
                // posizione dispari (indice 0,2,4...) -> si somma direttamente
                sum += n;
            } else {
                // posizione pari -> raddoppio e correzione se > 9
                n = n * 2;
                if (n > 9) n = n - 9;
                sum += n;
            }
        }
        // Ultima cifra (indice 10) = cifra di controllo
        const checkDigit = (10 - (sum % 10)) % 10;
        return checkDigit === parseInt(piva.charAt(10), 10);
    }

    private static isValidStandardFiscalCode(cf: string): boolean {
        if (!/^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/.test(cf)) {
            return false;
        }

        const oddMap: { [key: string]: number } = {
            '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
            'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
            'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12, 'T': 14,
            'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23
        };

        const evenMap: { [key: string]: number } = {
            '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
            'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9,
            'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18, 'T': 19,
            'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25
        };

        let sum = 0;
        for (let i = 0; i < 15; i++) {
            const c = cf.charAt(i).toUpperCase();
            if (i % 2 === 0) {
                // Odd positions (1-based)
                sum += oddMap[c] ?? 0;
            } else {
                // Even positions (1-based)
                sum += evenMap[c] ?? 0;
            }
        }

        const checkChar = String.fromCharCode(65 + (sum % 26)); // A = 65
        return checkChar === cf.charAt(15);
    }

    private static isValidTemporaryFiscalCode(cf: string): boolean {
        return /^[X-Z][0-9]{10,15}$/.test(cf); // Extendable range: X1234567890... up to 16 chars
    }

    /*****************************************************
    static emailValidator(control: AbstractControl): ValidationErrors | null {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return UserValidators.patternValidator(emailRegex, { 'invalidEmail': true })(control);
    }

    static nicknameValidator(control: AbstractControl): ValidationErrors | null {
        const nicknameRegex = /^[A-Za-z0-9_.-]{1,}$/;
        return UserValidators.patternValidator(nicknameRegex, { 'invalidNickname': true })(control);
    }

    static passwordValidator(control: AbstractControl): ValidationErrors | null {
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\\\[\]{};':"|,.<>?]).{8,}$/;
        return UserValidators.patternValidator(passwordRegex, { 'invalidPassword': true })(control);
    }

    static nicknameBusyValidator(control: AbstractControl): ValidationErrors | null {
        // Simulate an asynchronous check for a busy nickname
        const busyNicknames = ['admin', 'user', 'test'];
        const isBusy = busyNicknames.includes(control.value);
        return isBusy ? { 'nicknameBusy': true } : null;
    }

    static emailBusyValidator(control: AbstractControl): ValidationErrors | null {
        // Simulate an asynchronous check for a busy email
        const busyEmails = ['admin@example.com', 'user@example.com', 'test@example.com'];
        const isBusy = busyEmails.includes(control.value);
        return isBusy ? { 'emailBusy': true } : null;
    }
    *****************************************************/
}
